import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [{ v: 30, l: "30 days" }, { v: 90, l: "90 days" }, { v: 365, l: "1 year" }];
const fmt$ = (v) => `$${Number(v || 0).toLocaleString()}`;

export default function FleetProfit({ token }) {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API}/marketing/fleet-profit?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [token, days]);

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading fleet profit…</div>;

  return (
    <div data-testid="fleet-profit" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display font-bold text-white flex items-center gap-2 mr-auto"><Car size={17} className="text-[#F2B705]" /> Fleet Profit — which cars actually make money</h3>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${days === r.v ? "bg-[#F2B705] text-black" : "bg-white/5 border border-white/10 text-white/60"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Revenue", d.totals.revenue], ["Maintenance", d.totals.maintenance], ["Other Expenses", d.totals.expenses], ["Fleet Profit", d.totals.profit]].map(([l, v]) => (
          <div key={l} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
            <div className="text-white/45 text-xs uppercase tracking-widest">{l}</div>
            <div className={`font-display font-black text-2xl mt-1.5 ${l === "Fleet Profit" ? (v >= 0 ? "text-green-400" : "text-red-400") : "text-white"}`}>{fmt$(v)}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
              <th className="py-2 pr-3">Vehicle</th>
              <th className="py-2 pr-3 text-right">Revenue</th>
              <th className="py-2 pr-3 text-right">Maintenance</th>
              <th className="py-2 pr-3 text-right">Expenses</th>
              <th className="py-2 pr-3 text-right">Profit</th>
              <th className="py-2 pr-3 text-right">ROI</th>
              <th className="py-2 pr-3 text-right">Avg Length</th>
              <th className="py-2 pr-3">Service</th>
              <th className="py-2 text-right">Utilization</th>
            </tr>
          </thead>
          <tbody>
            {d.vehicles.map((v) => (
              <tr key={v.vehicle_id} data-testid={`fleet-row-${v.vehicle_id}`} className="border-b border-white/5">
                <td className="py-2.5 pr-3">
                  <div className="text-white font-semibold">{v.vehicle}</div>
                  <div className="text-white/40 text-xs">{v.rented_days} rented day{v.rented_days === 1 ? "" : "s"} · {v.status}</div>
                </td>
                <td className="py-2.5 pr-3 text-right text-[#F2B705] font-semibold">{fmt$(v.revenue)}</td>
                <td className="py-2.5 pr-3 text-right text-white/70">{fmt$(v.maintenance)}</td>
                <td className="py-2.5 pr-3 text-right text-white/70">{fmt$(v.expenses)}</td>
                <td className={`py-2.5 pr-3 text-right font-bold ${v.profit > 0 ? "text-green-400" : v.profit < 0 ? "text-red-400" : "text-white/50"}`}>{fmt$(v.profit)}</td>
                <td className="py-2.5 pr-3 text-right text-white/60">{v.roi_pct != null ? `${v.roi_pct}%` : "—"}</td>
                <td className="py-2.5 pr-3 text-right text-white/70">{v.avg_rental_days ? `${v.avg_rental_days}d` : "—"}<div className="text-white/35 text-[11px]">{v.rentals || 0} rental{v.rentals === 1 ? "" : "s"}</div></td>
                <td className="py-2.5 pr-3 text-xs">
                  <div className="text-white/60">{v.last_service ? `Last: ${v.last_service.service} ${v.last_service.date}` : "No service logged"}</div>
                  <div className={v.next_service && v.next_service.includes("OVERDUE") ? "text-red-400 font-semibold" : "text-[#F2B705]/80"}>{v.next_service ? `Next: ${v.next_service}` : ""}</div>
                </td>
                <td className="py-2.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#F2B705]" style={{ width: `${v.utilization_pct}%` }} /></div>
                    <span className="text-white/70 text-xs w-10 text-right">{v.utilization_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-white/35 text-xs pt-3">ROI = period profit ÷ purchase price. Add purchase prices in the Fleet tab for ROI to appear.</p>
      </div>
    </div>
  );
}
